import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProject } from "./ProjectLayout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { MODULE_STATUS, SCOPE_META, SCOPE_ORDER } from "@/lib/discovery";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { BookOpen, ArrowRight, Plus, Trash2, Sparkles } from "lucide-react";

export default function Discovery() {
  const { project } = useProject();
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user?.role === "admin" || user?.role === "consultant";
  const [disc, setDisc] = useState(null);

  const load = () => api.get(`/projects/${project.id}/discovery`).then((r) => setDisc(r.data));
  useEffect(() => { load(); }, [project.id]);

  const removeModule = async (mkey, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this custom module and all its questions?")) return;
    try {
      await api.delete(`/projects/${project.id}/custom-modules/${mkey}`);
      toast.success("Custom module removed");
      load();
    } catch { toast.error("Could not remove module"); }
  };

  return (
    <div className="px-6 py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-bold text-slate-900">Guided Discovery</h2>
          <p className="mt-1 text-sm text-slate-500">Work through each module at your own pace. We'll explain concepts before asking, and your answers save automatically.</p>
        </div>
        {canManage && disc && <AddCustomModuleDialog projectId={project.id} modules={disc.modules} onAdded={load} />}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {disc?.modules.map((m) => {
          const st = MODULE_STATUS[m.status] || { label: m.status, tone: "slate" };
          return (
            <div key={m.key} role="button" tabIndex={0} data-testid={`module-card-${m.key}`} onClick={() => navigate(`/projects/${project.id}/discovery/${m.key}`)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/projects/${project.id}/discovery/${m.key}`); } }}
              className="group relative flex cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-6 text-left transition-all duration-200 hover:-translate-y-1 hover:border-sky-300 hover:shadow-md">
              <div className="flex items-start justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white">{m.code}</span>
                <div className="flex items-center gap-1.5">
                  {m.custom_module && <StatusBadge label="Custom" tone="violet" />}
                  {!m.out_of_scope && <StatusBadge label={st.label} tone={st.tone} />}
                  {m.out_of_scope && <StatusBadge label="Out of Scope" tone="amber" />}
                </div>
              </div>
              <h3 className="mt-4 font-heading text-base font-bold text-slate-900">{m.title}</h3>
              <p className="mt-1 line-clamp-2 flex-1 text-sm text-slate-500">{m.description}</p>
              {!m.out_of_scope && (
                <div className="mt-4">
                  <div className="mb-1.5 flex items-center justify-between text-xs font-semibold text-slate-500">
                    <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> {SCOPE_META[m.scope]?.label || m.scope}</span>
                    <span>{m.answered}/{m.total}</span>
                  </div>
                  <Progress value={m.progress} className="h-2" />
                </div>
              )}
              <div className="mt-4 flex items-center justify-between">
                <span className="flex items-center gap-1 text-sm font-semibold text-sky-600 group-hover:gap-2 transition-all">
                  {m.status === "not_started" ? "Start module" : "Continue"} <ArrowRight className="h-4 w-4" />
                </span>
                {canManage && m.custom_module && (
                  <button data-testid={`delete-module-${m.key}`} onClick={(e) => removeModule(m.key, e)} className="rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AddCustomModuleDialog({ projectId, modules, onAdded }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [scope, setScope] = useState("standard_config");
  const [after, setAfter] = useState(modules[modules.length - 1]?.key || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Module name is required"); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/custom-modules`, { title, description, scope, after_module: after });
      toast.success("Custom module added — open it to build questions");
      setOpen(false); setTitle(""); setDescription(""); setScope("standard_config");
      onAdded();
    } catch { toast.error("Could not create module"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-custom-module-btn" className="rounded-lg bg-violet-600 hover:bg-violet-700"><Plus className="mr-1.5 h-4 w-4" /> Add Custom Module</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add a custom business module</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-sm font-semibold">Module name</Label><Input data-testid="cm-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 rounded-lg" placeholder="e.g. Dealer Management, Property Management" /></div>
          <div><Label className="text-sm font-semibold">Business purpose / description</Label><Textarea data-testid="cm-description" value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1 rounded-lg" placeholder="Briefly, what does this area cover and why does the customer need it?" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label className="text-sm font-semibold">Scope level</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="cm-scope" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{SCOPE_ORDER.map((s) => <SelectItem key={s} value={s}>{SCOPE_META[s].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm font-semibold">Position (after)</Label>
              <Select value={after} onValueChange={setAfter}>
                <SelectTrigger data-testid="cm-position" className="mt-1 rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{modules.map((m) => <SelectItem key={m.key} value={m.key}>{m.code} · {m.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-start gap-2 rounded-lg bg-violet-50 p-3 text-xs text-violet-800">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0" /> After creating, open the module to add questions manually or use <strong>AI-suggest</strong> to draft a starter set.
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="cm-submit" onClick={submit} disabled={busy} className="rounded-lg bg-violet-600 hover:bg-violet-700">{busy ? "Creating…" : "Create module"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
